import React from "react";
import { Link } from "react-router-dom";
import {
  Globe,
  Mail,
  Clock,
  Bell,
  Target,
  PieChart,
  Smartphone,
  Megaphone,
  RefreshCw,
  Send,
  BarChart,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

const MarketingHub = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  const marketingSections = [
    {
      title: "Landing Page Customization",
      description: "Customize your public-facing landing page with branding, content, and promotions.",
      icon: <Globe />,
      link: "/admin/marketing/landing-page",
    },
    {
      title: "Email Broadcasts",
      description: "Create and send one-time email campaigns to your customer segments.",
      icon: <Mail />,
      link: "/admin/marketing/email-broadcasts",
    },
    {
      title: "Email Automation",
      description: "Set up automated email sequences for abandoned registrations and reducing churn.",
      icon: <Clock />,
      link: "/admin/marketing/automation",
    },
    {
      title: "Notifications",
      description: "Manage in-app and email notifications for important events and reminders.",
      icon: <Bell />,
      link: "/admin/marketing/notifications",
    },
    {
      title: "Customer Segmentation",
      description: "Create targeted segments based on behavior, history, and demographics.",
      icon: <Target />,
      link: "/admin/marketing/segmentation",
    },
    {
      title: "Marketing Analytics",
      description: "View performance metrics for your marketing campaigns and customer engagement.",
      icon: <BarChart />,
      link: "/admin/marketing/analytics",
    },
    {
      title: "SMS Campaigns",
      description: "Create and manage text message campaigns for time-sensitive communications.",
      icon: <Smartphone />,
      link: "/admin/marketing/sms",
    },
    {
      title: "Promotions & Discounts",
      description: "Create special offers, discount codes, and early bird pricing.",
      icon: <Megaphone />,
      link: "/admin/marketing/promotions",
    },
    {
      title: "Retention Strategies",
      description: "Tools and campaigns specifically focused on reducing churn and improving loyalty.",
      icon: <RefreshCw />,
      link: "/admin/marketing/retention",
    },
    {
      title: "Social Media Integration",
      description: "Connect and manage your social media accounts for cross-platform promotion.",
      icon: <Send />,
      link: "/admin/marketing/social",
    },
  ];

  const MarketingCard = ({ title, description, icon, link }) => (
    <Link
      to={link}
      className={`${
        isDarkMode
          ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl'
          : 'card bg-base-100 shadow-xl'
      } hover:bg-base-200 transition-all duration-300 group rounded-xl`}
      aria-labelledby={`section-${title}-title`}
    >
      <div className="card-body p-6">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
            {React.cloneElement(icon, { className: "w-6 h-6 text-primary" })}
          </div>
          <div>
            <h2
              id={`section-${title}-title`}
              className="card-title text-lg font-semibold group-hover:text-primary transition-colors"
            >
              {title}
            </h2>
            <p className="text-base-content/60 text-sm mt-1">{description}</p>
          </div>
        </div>
        <div className="card-actions justify-end mt-4">
          <button
            className="btn btn-sm btn-ghost group-hover:btn-primary transition-all duration-300"
            aria-label={`Configure ${title}`}
          >
            Configure
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4 ml-1"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className={`${
        isDarkMode
          ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl'
          : 'bg-base-100 shadow-xl'
      } rounded-box p-6`}>
        <div>
          <h1 className="text-4xl font-bold text-base-content flex items-center gap-3">
            <Megaphone className="h-10 w-10 text-primary" />
            Marketing Management
          </h1>
          <p className="text-base-content/60 mt-2">
            Engage your audience, grow your customer base, and increase registrations through various marketing channels
          </p>
        </div>
      </div>

      {/* Marketing Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {marketingSections.map((section) => (
          <MarketingCard key={section.title} {...section} />
        ))}
      </div>

      {/* Quick Tips */}
      <div className={`${
        isDarkMode
          ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl'
          : 'bg-base-100 shadow-xl'
      } rounded-box`}>
        <div className="card-body">
          <div className="flex items-center gap-2 text-primary mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
              />
            </svg>
            <h3 className="font-semibold text-base-content">Marketing Tips</h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-primary/60" />
              <span className="text-base-content/80">
                Start by customizing your landing page to align with your brand
              </span>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-primary/60" />
              <span className="text-base-content/80">
                Set up automated emails for abandoned registrations to recover lost revenue
              </span>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-primary/60" />
              <span className="text-base-content/80">
                Create customer segments to send more targeted communications
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MarketingHub; 